/** Minimal numeric design studies. Readings and measured history come from the host. */
export const simpleNumericDesignNames = Object.freeze({
  focus: "Focus",
  line: "Mini Trace",
  capsule: "Capsule",
});

const coral = "#d9775f";
const white = "#f5f5f7";
const muted = "#a4a4ab";
const quiet = "#77777d";
const fixed = (value, digits = 1) => Number(value).toFixed(digits);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function text(x, y, value, size = 11, color = muted, extra = "") {
  return `<text x="${x}" y="${y}" font-size="${size}" fill="${color}" ${extra}>${value}</text>`;
}

function focus() {
  return `${text(180, 41, "SPEED OVER GROUND", 10, quiet, 'text-anchor="middle" letter-spacing="1.6"')}
    ${text(180, 148, "—", 96, white, 'data-part="speed" text-anchor="middle" font-weight="550" letter-spacing="-4"')}
    ${text(180, 181, "kn", 19, muted, 'text-anchor="middle"')}
    ${text(180, 225, "", 11, muted, 'data-part="change" text-anchor="middle"')}`;
}

function line() {
  return `${text(29, 41, "SPEED OVER GROUND", 10, quiet, 'letter-spacing="1.6"')}
    <g data-part="readout" transform="translate(29 0)">
      ${text(0, 139, "—", 89, white, 'data-part="speed" font-weight="550" letter-spacing="-4"')}
      ${text(159, 135, "kn", 21, muted, 'data-part="unit"')}
    </g>
    ${text(330, 165, "", 11, muted, 'data-part="change" text-anchor="end"')}
    <path data-part="trace" d="M30 201H330" fill="none" stroke="${coral}" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
    <circle data-part="trace-dot" cx="330" cy="201" r="2.5" fill="${coral}"/>
    ${text(30, 232, "", 9, quiet, 'data-part="history-span"')}
    ${text(330, 232, "now", 9, quiet, 'text-anchor="end"')}`;
}

function capsule() {
  return `${text(180, 41, "SPEED OVER GROUND", 10, quiet, 'text-anchor="middle" letter-spacing="1.6"')}
    <rect x="25" y="75" width="310" height="112" rx="56" fill="#28282b"/>
    <g data-part="readout" transform="translate(102 0)">
      ${text(0, 151, "—", 70, white, 'data-part="speed" font-weight="550" letter-spacing="-2"')}
      ${text(129, 148, "kn", 18, muted, 'data-part="unit"')}
    </g>
    ${text(180, 226, "", 11, muted, 'data-part="change" text-anchor="middle"')}`;
}

/** Responsive SVG with no timers, external resources or generated telemetry. */
export function createSimpleNumericDesign(variant, uid = variant) {
  if (!Object.hasOwn(simpleNumericDesignNames, variant))
    throw new Error(`Unknown simple numeric design: ${variant}`);
  const safeUid = `tc-simple-number-${String(uid).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const faces = { focus, line, capsule };
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 260" data-simple-numeric-design="${variant}" data-design-uid="${safeUid}" role="img" aria-label="${simpleNumericDesignNames[variant]}: speed over ground" style="display:block;width:100%;height:auto;font-family:TCInter,Inter,-apple-system,BlinkMacSystemFont,sans-serif;font-variant-numeric:tabular-nums">${faces[variant]()}</svg>`;
}

function part(root, name) {
  return root.querySelector(`[data-part="${name}"]`);
}
function setText(root, name, value) {
  const node = part(root, name);
  if (node) node.textContent = value;
}
function setAttr(root, name, attribute, value) {
  const node = part(root, name);
  if (node) node.setAttribute(attribute, String(value));
}

function durationLabel(samples) {
  const seconds = Math.max(
    0,
    (samples.at(-1).measuredAt - samples[0].measuredAt) / 1000,
  );
  if (seconds < 1) return "one sample";
  if (seconds < 120) return `${Math.round(seconds)} s of history`;
  return `${fixed(seconds / 60)} min of history`;
}

function historySamples(reading, history) {
  const samples = (Array.isArray(history) ? history : [])
    .filter(
      (entry) =>
        Number.isFinite(entry?.speed) &&
        entry.speed >= 0 &&
        Number.isFinite(entry?.measuredAt),
    )
    .map((entry) => ({ speed: entry.speed, measuredAt: entry.measuredAt }))
    .sort((a, b) => a.measuredAt - b.measuredAt)
    .slice(-60);
  const measuredAt = Number.isFinite(reading.measuredAt)
    ? reading.measuredAt
    : (samples.at(-1)?.measuredAt ?? 0);
  // The current interpolated endpoint replaces the incoming final measurement.
  // The trace therefore travels with the number as the host animates an update.
  return [
    ...samples.filter((entry) => entry.measuredAt < measuredAt),
    { speed: reading.speed, measuredAt },
  ];
}

function changeLabel(speed, samples) {
  if (samples.length < 2) return "First reading";
  const delta = speed - samples[0].speed;
  if (Math.abs(delta) < 0.05) return "— steady since first sample";
  return `${delta > 0 ? "↗" : "↘"} ${delta > 0 ? "+" : "−"}${fixed(Math.abs(delta))} kn · since first sample`;
}

function curve(points) {
  return points.reduce((path, point, index) => {
    if (!index) return `M${fixed(point.x, 2)} ${fixed(point.y, 2)}`;
    const previous = points[index - 1];
    const middle = (previous.x + point.x) / 2;
    return `${path} C${fixed(middle, 2)} ${fixed(previous.y, 2)} ${fixed(middle, 2)} ${fixed(point.y, 2)} ${fixed(point.x, 2)} ${fixed(point.y, 2)}`;
  }, "");
}

/** Draw the host's current interpolated reading. No internal animation clock. */
export function updateSimpleNumericDesign(
  root,
  variant,
  reading,
  history = [],
) {
  if (!Object.hasOwn(simpleNumericDesignNames, variant))
    throw new Error(`Unknown simple numeric design: ${variant}`);
  if (root?.getAttribute?.("data-simple-numeric-design") !== variant)
    root = root?.querySelector?.(`[data-simple-numeric-design="${variant}"]`);
  if (!root) throw new Error("Simple numeric design SVG was not found");
  const speed = reading?.speed;
  if (!Number.isFinite(speed) || speed < 0) {
    root.setAttribute("aria-label", "Speed over ground unavailable");
    root.setAttribute("opacity", ".4");
    setText(root, "speed", "—");
    setText(root, "change", "Unavailable");
    return;
  }
  root.removeAttribute("opacity");
  root.setAttribute(
    "aria-label",
    `${simpleNumericDesignNames[variant]}: ${fixed(speed)} knots speed over ground`,
  );
  const samples = historySamples(reading, history);
  const display = speed < 1e6 ? fixed(speed) : speed.toExponential(1);
  setText(root, "speed", display);
  setText(root, "change", changeLabel(speed, samples));
  const maximumSize = { focus: 96, line: 89, capsule: 70 }[variant];
  const size = Math.min(maximumSize, 280 / (display.length * 0.58));
  setAttr(root, "speed", "font-size", fixed(size, 2));
  if (variant === "focus") return;

  const numberWidth = size * (display.length * 0.58 - 0.28);
  const unitX = numberWidth + 13;
  setAttr(root, "unit", "x", fixed(unitX, 2));
  if (variant === "capsule") {
    setAttr(
      root,
      "readout",
      "transform",
      `translate(${fixed(180 - (unitX + 23) / 2, 2)} 0)`,
    );
    return;
  }

  const values = samples.map((entry) => entry.speed);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = Math.max(0.2, high - low);
  const firstTime = samples[0].measuredAt;
  const timeSpan = samples.at(-1).measuredAt - firstTime;
  const points = samples.map((entry, index) => ({
    x:
      timeSpan > 0
        ? 30 + ((entry.measuredAt - firstTime) / timeSpan) * 300
        : 330 - (samples.length - 1 - index) * 300,
    y:
      high - low < 0.2
        ? 199.5 - ((entry.speed - low) / 0.2) * 29
        : 214 - clamp((entry.speed - low) / span, 0, 1) * 29,
  }));
  const linePath =
    points.length < 2 ? `M330 ${fixed(points[0].y, 2)}` : curve(points);
  setAttr(root, "trace", "d", linePath);
  setAttr(root, "trace-dot", "cy", fixed(points.at(-1).y, 2));
  setText(root, "history-span", durationLabel(samples));
}
